"use client";

import { useEffect, useRef } from "react";
import { sendGAEvent } from "@next/third-parties/google";
import { useUser } from "@/contexts/user-context";
import { isAuthPersistent } from "@/utils/analytics";

interface ProductData {
  id?: string | number;
  name?: string;
  base_price_amount?: number | string;
  product_type_name?: string;
  product_subtype_details?: {
    name?: string;
  };
  vendor_details?: {
    business_name?: string;
  };
  [key: string]: unknown;
}

const GoogleAnalyticsWrapper = ({
  productData,
  children,
}: {
  productData: ProductData;
  children: React.ReactNode;
}) => {
  const { isAuthenticated } = useUser();
  const lastFiredProductId = useRef<string | number | null>(null);

  const productId = productData?.id;

  useEffect(() => {
    if (!productId) return;

    // Deduplicate: fire only once per product view
    if (lastFiredProductId.current === productId) {
      return;
    }
    lastFiredProductId.current = productId;

    const isAuth = isAuthenticated || isAuthPersistent();

    sendGAEvent("event", "view_item", {
      currency: "INR",
      value: Number(productData.base_price_amount),
      user_auth_state: isAuth ? "logged_in" : "logged_out",
      items: [
        {
          item_id: String(productData.id),
          item_name: productData.name,
          item_brand: productData.vendor_details?.business_name,
          item_category: productData.product_type_name,
          item_category2: productData.product_subtype_details?.name,
          price: Number(productData.base_price_amount),
          quantity: 1,
        },
      ],
    });
    // Deliberately depend only on stable product identity (productId)
    // so auth transitions or object re-creations do not cause duplicate view_item events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  return <>{children}</>;
};

export default GoogleAnalyticsWrapper;
